import { RatingSummary } from '@atoms/RatingSummary';
import formatPhoneLink from '@utils/format-phone-link';
import React, { useState } from 'react';
import { useLocator } from '../../contexts/franchise/locator-provider';
import ChangeLocationDrawer from './change-location-drawer';
import {
    CHANGE_LOCATION,
    YOUR_DETECTED_LOCATION,
    YOUR_SELECTED_LOCATION,
    LOCAL_CITY_LOCATION,
    NATIONAL_CALL_CENTER_DISPLAY,
    NATIONAL_CALL_CENTER_LOCATION,
    PHONE_NUMBER,
    PHONE_NUMBER_DISPLAY,
    AVERAGE_RATE_THRESHOLD,
} from '../../constants/constants';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import triggerInvoca from '../utils/trigger-invoca';
import RowSet from '../primitives/grid/row-set';
import Color, { colors } from '../primitives/colors';
import Skeleton from '../primitives/skeleton';
import Typography from '../primitives/typography';
import PhoneNumber from '../molecules/phone-number';
import { StyledButtonAsLink } from '../primitives/styled-button';
import StyledPillButton, { StyledPillButtonAsLink } from '../primitives/styled-pill-button';
import Divider from '../primitives/divider';

const FooterLocationDetail = ({ isVisible, onClose, localCtaData }) => {
    const {
        loading: geoIsLoading,
        geo: locatorGeo,
        franchise: locatorFranchise,
        locationChangedByUser,
        isNationalCallCenter,
    } = useLocator();

    const { hideNationalCallCenter, hideFranchiseLink } = useCtaProvider();
    const { isMicrosite, isLocalCityPage, franchise: micrositeFranchise } = useMicrosite();

    const geo = localCtaData?.geo ? localCtaData?.geo : locatorGeo;
    const franchise = localCtaData?.franchise || micrositeFranchise || locatorFranchise?.data;
    const disableChangeLocation = (localCtaData?.geo && localCtaData?.franchise) || micrositeFranchise;

    const {
        name: frName,
        mainPhone: frPhone,
        reviewCount,
        averageRating,
        permalink: frLink,
        franchiseNumber: frId,
    } = franchise;

    const [locationChangeVisible, setLocationChangeVisible] = useState(false);

    React.useEffect(() => {
        triggerInvoca();
    }, [isVisible]);

    return (
        <>
            {(isVisible && !locationChangeVisible) && (
                <div css={styles.locationDetailWrap}>
                    <div css={styles.locationDetail(isVisible)}>
                        <button css={styles.closeButton} onClick={onClose}>
                            <div css={styles.closeButtonRow} />
                            <div css={styles.closeButtonRow} />
                        </button>
                        <RowSet breakpoints={{ dt: { between: 20 } }}>
                            <Typography fontSize="2xl" fontWeight="medium" color="primaryOrange">
                                24/7 Emergency Service
                            </Typography>
                            <Divider />
                            <RowSet
                                breakpoints={{ dt: { between: 4 } }}
                                css={{
                                    paddingRight: '16px',
                                }}
                            >
                                <Color color="primaryOrange">
                                    {(isLocalCityPage && !hideFranchiseLink) && LOCAL_CITY_LOCATION}
                                    {(locationChangedByUser && !isLocalCityPage
                                        && !hideFranchiseLink && !isNationalCallCenter)
                                        && YOUR_SELECTED_LOCATION}
                                    {(!locationChangedByUser && !isLocalCityPage
                                        && !hideFranchiseLink && !isNationalCallCenter)
                                        && YOUR_DETECTED_LOCATION}
                                    {((hideFranchiseLink || isNationalCallCenter)
                                        && !isLocalCityPage) && NATIONAL_CALL_CENTER_LOCATION}
                                </Color>
                                {(!isNationalCallCenter && !isLocalCityPage && !hideFranchiseLink) && (
                                    <Skeleton css={{ height: '32px' }}>
                                        {(!geoIsLoading
                                            && geo?.city
                                            && frName
                                            && !locationChangedByUser
                                        ) && (
                                            <Typography fontSize="3xl" fontWeight="medium">
                                                {geo.city}
                                                {geo?.stateShort ? ', ' : ' '}
                                                {geo?.stateShort || ''}
                                            </Typography>
                                        )}
                                        {(!geoIsLoading
                                            && geo?.city
                                            && frName
                                            && locationChangedByUser
                                        ) && (
                                            <Typography fontSize="3xl" fontWeight="medium">
                                                {frName}
                                            </Typography>
                                        )}
                                    </Skeleton>
                                )}
                                {isLocalCityPage && (
                                    <Skeleton css={{ height: '32px' }}>
                                        <Typography fontSize="3xl" fontWeight="medium">
                                            {franchise?.address?.city}
                                            {franchise?.address?.state ? ', ' : ' '}
                                            {franchise?.address?.state || ''}
                                        </Typography>
                                    </Skeleton>
                                )}
                                {((hideFranchiseLink || isNationalCallCenter) && !isLocalCityPage) && (
                                    <Typography fontSize="3xl" fontWeight="medium">
                                        {NATIONAL_CALL_CENTER_DISPLAY}
                                    </Typography>
                                )}
                                {((!isNationalCallCenter && !hideFranchiseLink && !locationChangedByUser) || isLocalCityPage) && (
                                    <Skeleton>
                                        {frName && <Typography fontSize="base">{frName}</Typography>}
                                    </Skeleton>
                                )}
                                {(reviewCount !== 0 && !hideFranchiseLink && !isNationalCallCenter && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD) && (
                                    <RatingSummary count={reviewCount} rating={averageRating} />
                                )}
                            </RowSet>
                            <Skeleton
                                css={{
                                    width: '256px',
                                    height: '48px',
                                }}
                                isButton
                                backgroundColor="primaryOrange"
                            >
                                {(frPhone && !hideFranchiseLink) && (
                                    <PhoneNumber
                                        as={StyledButtonAsLink}
                                        to={`tel:${formatPhoneLink(frPhone) as string}`}
                                    >
                                        {formatPhoneLink(frPhone)}
                                    </PhoneNumber>
                                )}
                                {hideFranchiseLink && (
                                    <PhoneNumber
                                        as={StyledButtonAsLink}
                                        to={`tel:${PHONE_NUMBER as string}`}
                                    >
                                        {PHONE_NUMBER_DISPLAY}
                                    </PhoneNumber>
                                )}
                            </Skeleton>
                        </RowSet>
                        <RowSet
                            breakpoints={{ dt: { between: 18 } }}
                            css={{
                                textAlign: 'left',
                                width: 'unset',
                                marginTop: '18px',
                            }}
                        >
                            {!!frId && !hideFranchiseLink && (
                                <>
                                    <StyledPillButtonAsLink
                                        to={frLink}
                                    >
                                        Find Out More ›
                                    </StyledPillButtonAsLink>
                                    <Divider />
                                </>
                            )}
                            {(!disableChangeLocation && !hideFranchiseLink && !isLocalCityPage) && (
                                <div>
                                    <div>
                                        Need help elsewhere?
                                        {' '}
                                    </div>
                                    <StyledPillButton
                                        className="change-location-open-cta"
                                        onClick={() => {
                                            setLocationChangeVisible(true);
                                        }}
                                    >
                                        {CHANGE_LOCATION}
                                        {' '}
                                        ›
                                    </StyledPillButton>
                                </div>
                            )}
                            {isLocalCityPage && (
                                <div>
                                    <div>
                                        Need help elsewhere?
                                        {' '}
                                    </div>
                                    <StyledPillButtonAsLink
                                        to="/locations"
                                    >
                                        {CHANGE_LOCATION}
                                        {' '}
                                        ›
                                    </StyledPillButtonAsLink>
                                </div>
                            )}
                            {((!hideNationalCallCenter && !isMicrosite && !isNationalCallCenter && !hideFranchiseLink)
                                || isLocalCityPage
                            ) && (
                                <>
                                    <Divider />
                                    <Typography fontSize="3xl" fontWeight="medium">
                                        {NATIONAL_CALL_CENTER_DISPLAY}
                                    </Typography>
                                    <StyledButtonAsLink
                                        to={`tel:${PHONE_NUMBER_DISPLAY}`}
                                    >
                                        {PHONE_NUMBER_DISPLAY}
                                    </StyledButtonAsLink>
                                </>
                            )}
                        </RowSet>
                    </div>
                </div>
            )}
            <ChangeLocationDrawer
                visible={locationChangeVisible && isVisible}
                onClose={() => setLocationChangeVisible(false)}
            />
        </>
    );
};

const styles = {
    locationDetailWrap: {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: 300,
        overflowY: 'auto',
    },
    closeButton: {
        position: 'absolute',
        right: '1.25rem',
        zIndex: 30,
        width: '2rem',
        height: '2rem',
        top: '30px',
    },
    closeButtonRow: {
        position: 'absolute',
        top: '0',
        right: '50%',
        transform: 'rotate(45deg)',
        width: '3px',
        height: '26px',
        backgroundColor: colors.shadesBlack,
        '&:last-of-type': {
            transform: 'rotate(-45deg)',
        },

    },
    locationDetail: isVisible => ({
        position: 'relative',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        paddingBottom: '1.5rem',
        width: '100%',
        backgroundColor: colors.shadesWhite,
        transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        maxWidth: '100vw',
        minHeight: '100vh',
        paddingTop: '74px',
        transform: !isVisible ? 'translateY(100%)' : 'translateY(0)',
        '@media (min-width: 375px)': {
            paddingLeft: '1.75rem',
            paddingRight: '1.75rem',
        },
    }),
};

export default FooterLocationDetail;
